/**
 * confirmDialog — promise-returning confirm helper.
 *
 * Threading dialog open/close state + a deferred resolver through every
 * call site is noisy when all the caller wants is a yes/no answer.
 * `openConfirmDialog({...}).then(ok => …)` keeps the caller linear:
 * await it, branch on the boolean. The dialog mounts itself in a portal
 * and tears down on resolve.
 *
 * Used by the critical-gap gate in ReportPreferencesStep, but kept
 * generic (no SiteRAG references) so other surfaces can reuse it.
 */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Button } from './button';
import { AlertTriangle } from 'lucide-react';

export interface ConfirmDialogOptions {
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' tints the confirm button red — use for destructive
   *  actions; 'default' uses the primary colour. */
  tone?: 'default' | 'danger';
}

export function openConfirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false);

  return new Promise<boolean>((resolve) => {
    const host = document.createElement('div');
    host.dataset.confirmDialogHost = 'true';
    document.body.appendChild(host);
    const root: Root = createRoot(host);

    let settled = false;
    const close = (answer: boolean) => {
      if (settled) return;
      settled = true;
      try {
        root.unmount();
      } catch {
        /* unmount races are harmless — the host removal below cleans up */
      }
      if (host.parentNode) host.parentNode.removeChild(host);
      resolve(answer);
    };

    root.render(
      <ConfirmDialog
        options={options}
        onConfirm={() => close(true)}
        onCancel={() => close(false)}
      />,
    );
  });
}

const ConfirmDialog: React.FC<{
  options: ConfirmDialogOptions;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ options, onConfirm, onCancel }) => {
  const { title, body, confirmLabel = 'Continue', cancelLabel = 'Cancel', tone = 'default' } = options;

  // Esc → cancel; Enter → confirm. The caller is opening the dialog
  // from inside another modal (the wizard) so we don't trap focus —
  // base UI scroll lock is enough.
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      if (e.key === 'Enter')  { e.preventDefault(); onConfirm(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel, onConfirm]);

  return (
    <div
      data-testid="confirm-dialog"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60"
      onClick={(e) => {
        // Click on backdrop only — clicks inside the panel shouldn't dismiss.
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-background w-[480px] max-w-[90vw] border border-border shadow-2xl">
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${tone === 'danger' ? 'text-danger' : 'text-warning'}`} />
          <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">{title}</h2>
        </div>
        <div className="px-5 py-4 text-[11px] text-text-secondary leading-relaxed space-y-2">
          {body}
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-[9px] uppercase tracking-widest"
            data-testid="confirm-dialog-cancel"
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'mission-danger' : 'default'}
            size="sm"
            className="h-8 text-[9px] uppercase tracking-widest"
            data-testid="confirm-dialog-confirm"
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
